import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { TimeEntry } from '@tt/shared';
import { Combobox } from '@/components/combobox';
import { DurationInput } from '@/components/duration-input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input, Textarea } from '@/components/ui/input';
import { Alert } from '@/components/ui/misc';
import { isApiError } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { useCreateEntry, useDeleteEntry, useOptions, useUpdateEntry } from '@/lib/queries';
import { useFormat } from '@/lib/use-format';
import { useTimesheetLock } from '@/lib/use-timesheet-lock';
import { NewActivityDialog } from './new-activity-dialog';
import { useOptionItems } from './use-option-items';

interface EntryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing entry to edit; omit to create. */
  entry?: TimeEntry | null;
  /** Defaults for a new entry. */
  defaults?: { entryDate: string; projectId?: string; activityId?: string };
}

/** Create / edit dialog used by the day view; the week grid edits its cells in place. */
export function EntryFormDialog({ open, onOpenChange, entry, defaults }: EntryFormDialogProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const create = useCreateEntry();
  const update = useUpdateEntry();
  const remove = useDeleteEntry();
  const lock = useTimesheetLock();

  const [entryDate, setEntryDate] = useState(defaults?.entryDate ?? '');
  const [projectId, setProjectId] = useState<string | null>(null);
  const [activityId, setActivityId] = useState<string | null>(null);
  const [minutes, setMinutes] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [newActivity, setNewActivity] = useState<{ open: boolean; name: string }>({
    open: false,
    name: '',
  });

  const { data: options } = useOptions(entryDate || undefined);
  const items = useOptionItems(options);

  // Reset the form whenever the dialog opens for a (different) entry.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setFormError(null);
    if (entry) {
      setEntryDate(entry.entryDate);
      setProjectId(entry.project.id);
      setActivityId(entry.activity.id);
      setDescription(entry.description ?? '');
      setMinutes(entry.durationMinutes);
    } else {
      setEntryDate(defaults?.entryDate ?? '');
      setProjectId(defaults?.projectId ?? null);
      setActivityId(defaults?.activityId ?? null);
      setDescription('');
      setMinutes(null);
    }
  }, [open, entry, defaults]);

  const isPending = create.isPending || update.isPending || remove.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!projectId) next.projectId = t('time.selectProject');
    if (!activityId) next.activityId = t('time.selectActivity');
    if (minutes === null || minutes <= 0) next.durationMinutes = t('time.invalidDuration');
    // The date can be typed, so it can be moved into a closed period from an open day. Said here
    // rather than let the server say it, so the answer lands on the field that caused it.
    if (lock.isLocked(entryDate)) next.entryDate = t('time.closedDate');
    setErrors(next);
    if (Object.keys(next).length) return;

    const body = {
      durationMinutes: minutes!,
      projectId: projectId!,
      activityId: activityId!,
      entryDate,
      description: description.trim() || null,
    };
    const onError = (err: unknown) => {
      if (isApiError(err)) {
        setErrors(err.fieldErrors);
        setFormError(Object.keys(err.fieldErrors).length ? null : describeError(err, t));
      } else setFormError(describeError(err, t));
    };
    const onSuccess = () => {
      toast.success(t('time.entrySaved'));
      onOpenChange(false);
    };
    if (entry) update.mutate({ id: entry.id, ...body }, { onSuccess, onError });
    else create.mutate(body, { onSuccess, onError });
  };

  const del = () => {
    if (!entry || !window.confirm(t('time.deleteEntryConfirm'))) return;
    remove.mutate(entry.id, {
      onSuccess: () => {
        toast.success(t('time.entryDeleted'));
        onOpenChange(false);
      },
      onError: (err) => setFormError(describeError(err, t)),
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent aria-describedby={undefined}>
          <form onSubmit={submit} className="space-y-4" noValidate>
            <DialogHeader>
              <DialogTitle>{entry ? t('time.editEntry') : t('time.newEntry')}</DialogTitle>
            </DialogHeader>
            {formError ? <Alert variant="error">{formError}</Alert> : null}

            <Field label={t('time.project')} error={errors.projectId} required>
              {(p) => (
                <Combobox
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  aria-invalid={p['aria-invalid']}
                  value={projectId}
                  // Changing the project drops the activity with it: an activity belongs to one
                  // project, so one chosen under the old project is no longer a choice.
                  onChange={(v) => {
                    if (v !== projectId) setActivityId(null);
                    setProjectId(v);
                  }}
                  items={items.projects}
                  recentValues={items.recentProjects}
                  placeholder={t('time.selectProject')}
                />
              )}
            </Field>
            <Field label={t('time.activity')} error={errors.activityId} required>
              {(p) => (
                <Combobox
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  aria-invalid={p['aria-invalid']}
                  value={activityId}
                  onChange={setActivityId}
                  items={items.activitiesFor(projectId)}
                  recentValues={items.recentActivitiesFor(projectId)}
                  placeholder={projectId ? t('time.selectActivity') : t('time.selectProjectFirst')}
                  disabled={!projectId}
                  onCreate={(name) => setNewActivity({ open: true, name })}
                  createLabel={(name) => t('time.createActivity', { name })}
                />
              )}
            </Field>
            <Field label={t('time.date')} error={errors.entryDate} required>
              {(p) => (
                <Input
                  {...p}
                  type="date"
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  required
                />
              )}
            </Field>

            <Field
              label={t('time.duration')}
              hint={t('time.durationHelp')}
              error={errors.durationMinutes}
              required
            >
              {(p) => (
                <DurationInput
                  {...p}
                  value={minutes}
                  hoursFormat={fmt.hoursFormat}
                  onChange={(mins) => setMinutes(mins)}
                  className="w-40"
                />
              )}
            </Field>

            <Field
              label={`${t('time.description')} (${t('app.optional')})`}
              error={errors.description}
            >
              {(p) => (
                <Textarea
                  {...p}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('time.descriptionPlaceholder')}
                  maxLength={2000}
                />
              )}
            </Field>

            <DialogFooter className="sm:justify-between">
              <div>
                {entry ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive"
                    onClick={del}
                    disabled={isPending}
                  >
                    {t('app.delete')}
                  </Button>
                ) : null}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {t('app.cancel')}
                </Button>
                <Button type="submit" disabled={isPending}>
                  {t('app.save')}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <NewActivityDialog
        open={newActivity.open}
        onOpenChange={(o) => setNewActivity((s) => ({ ...s, open: o }))}
        projectId={projectId}
        projectName={options?.projects.find((p) => p.id === projectId)?.name ?? undefined}
        initialName={newActivity.name}
        onCreated={(a) => setActivityId(a.id)}
      />
    </>
  );
}
